import { createElement, Fragment, useEffect, useRef } from 'react';
import { CompositeInputProps, Mode } from './';
import { Button, ButtonProps } from '../Button';
import { InputProps } from '../Input';
import { BaseInput, BaseInputProps } from '../Input/__internal__/BaseInput';
import { InvisibleText } from '../InvisibleText';
import { useKeyboardTooltip } from '../Settings';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';

import bundle from './NovaComponents-CompositeInput-gui-icu.properties';

// Import last
import cx from 'classnames';
import cn from './CompositeInput.css.json';
import './CompositeInput.css';

const useTextLocalizer = createUseTextLocalizer(bundle);
const noop = () => {};

const modeInputProps: { [key in Mode]: Partial<InputProps> } = {
  select: {
    editable: false,
    variant: 'composite',
  },
  combo: {
    variant: 'composite',
    editable: true,
  },
  readonly: {
    editable: false,
  },
};

const useDescribedById = createUseHtmlId('CompositeInput-describedby');

export const CompositeInput = (props: CompositeInputProps) => {
  const localize = useTextLocalizer();
  const describedById = useDescribedById();
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const {
    value,
    mode = 'select',
    disabled,
    commitOnEnterInDialog,
    entryFieldProps,
    entryFieldRef,
    onOpen,
    buttonDisplay,
    buttonProps,
    buttonRef,
    testId,
    ariaLabel,
    ariaLabelledBy,
    ariaRoleDescription,
    ariaDescribedBy,
    className,
    ...restOfProps // For things like style and className
  } = props;

  const readonly = mode === 'readonly';

  const describedByText =
    mode === 'select' || mode === 'combo' ? localize(`${mode}.mode.description.aria`) : '';

  const defaultDescribedBy = readonly ? undefined : [describedById];
  const isValidDescribedByArray =
    Array.isArray(ariaDescribedBy) && ariaDescribedBy.join('').length > 0;
  const isValidDescribedByString =
    typeof ariaDescribedBy === 'string' && ariaDescribedBy.length > 0;
  const computedAriaDescribedBy = isValidDescribedByArray
    ? ariaDescribedBy
    : isValidDescribedByString
    ? [ariaDescribedBy]
    : defaultDescribedBy;

  const { keyboardTooltip, ...restEntryFieldProps } = entryFieldProps ?? {};
  const computedInputProps: BaseInputProps = {
    onChange: noop, // let entryFieldProps override if it has it
    ...restEntryFieldProps,
    enableAutoComplete: false,
    value,
    disabled,
    commitOnEnterInDialog: commitOnEnterInDialog || mode === 'select',
    className: cx(cn.input, restEntryFieldProps?.className),
    ...modeInputProps[mode],
  };

  const computedButtonProps: ButtonProps = {
    tooltip: localize('button.default.tooltip'),
    ...buttonProps,
    text: typeof buttonDisplay === 'string' ? buttonDisplay : undefined,
    icon: typeof buttonDisplay !== 'string' ? { icon: buttonDisplay } : undefined,
    disabled,
    className: cx(cn.button, mode !== 'select' && mode !== 'readonly' && cn.editableButton),
    focusable: false,
    onPress: onOpen,
  };

  // Trigger onOpen on certain keyboard interactions.
  // It would be nicer if BaseInput provided support for this scenario directly but this
  // is the quickest way to get where we need to.
  const preventOpenViaKeyboard = readonly || disabled;
  useEffect(() => {
    if (preventOpenViaKeyboard) return;
    const handler = (e: KeyboardEvent) => {
      const altArrowPress = e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown');
      const openForSelectMode = mode === 'select' && (e.key === 'Enter' || altArrowPress);
      const openForComboMode = mode === 'combo' && altArrowPress;
      if (openForSelectMode || openForComboMode) onOpen?.();
    };
    internalInputRef?.current?.addEventListener('keyup', handler);
    return () => {
      internalInputRef?.current?.removeEventListener('keyup', handler);
    };
  }, [mode, preventOpenViaKeyboard, onOpen]);

  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip:
      keyboardTooltip ??
      (mode === 'select' || mode === 'combo'
        ? localize(`${mode}.mode.keyboard.tooltip.label`)
        : ''),
  });

  return (
    <div
      data-testid={testId}
      className={cx(cn.container, className)}
      role="group"
      aria-roledescription={ariaRoleDescription}
      {...restOfProps}
    >
      <BaseInput
        {...computedInputProps}
        ariaLabel={ariaLabel}
        ariaLabelledBy={ariaLabelledBy}
        ariaDescribedBy={computedAriaDescribedBy}
        testId={postfixTestId(testId, 'input')}
        ref={(e) => {
          internalInputRef.current = e;
          setRef(entryFieldRef, e);
          setKeyboardTooltipRef(e);
        }}
      />
      {!readonly ? (
        <Fragment>
          <Button
            {...computedButtonProps}
            testId={postfixTestId(testId, 'button')}
            ref={(e) => setRef(buttonRef, e)}
          />
          <InvisibleText id={describedById}>{describedByText}</InvisibleText>
        </Fragment>
      ) : null}
    </div>
  );
};
